/**
 * alpine-defer.js — x-defer plugin for Alpine.js
 *
 * Defers the initialisation of any Alpine component (`x-data` element) until
 * one of four trigger conditions fires.  Place the attribute on the SAME
 * element that carries `x-data`.
 *
 * Modes:
 *   x-defer="intersect"         — init when element enters the viewport
 *   x-defer="interact"          — init on first user interaction (touchstart /
 *                                  mouseover / wheel / scroll / keydown)
 *   x-defer="idle"              — init via requestIdleCallback (4 s fallback)
 *   x-defer="event:my-event"    — init when `my-event` fires on window
 *
 * Usage:
 *   <script src="{{ 'alpine-defer.js' | asset_url }}" defer></script>
 *   <!-- load BEFORE alpine.min.js -->
 *
 * Then on any Alpine root element:
 *   <div x-data="myComponent()" x-defer="interact">…</div>
 *   <div x-data="myComponent()" x-defer="idle">…</div>
 *   <div x-data="myComponent()" x-defer="event:cart:open">…</div>
 *   <div x-data="myComponent()" x-defer="intersect">…</div>
 */
(function () {
  'use strict';

  /** Idle timeout (ms) — initialise even if requestIdleCallback never fires */
  const IDLE_TIMEOUT = 4000;

  /**
   * Stash the x-data value, replace the attribute with a sentinel so Alpine
   * skips the element, then return a `hydrate` function the caller invokes
   * once the trigger condition is met.
   *
   * @param {Element} el
   * @returns {() => void} hydrate
   */
  function defer(el) {
    // Already processed (guard against double-registration)
    if (el.__gjDeferHydrated) return () => {};

    const xDataValue = el.getAttribute('x-data');

    // Remove x-data so Alpine.js ignores the element during its initial walk
    el.removeAttribute('x-data');
    // x-ignore tells Alpine not to descend into children either
    el.setAttribute('x-ignore', '');

    return function hydrate() {
      if (el.__gjDeferHydrated) return;
      el.__gjDeferHydrated = true;

      el.removeAttribute('x-ignore');
      // Alpine sets _x_ignore as a JS property when it processes x-ignore.
      // Removing the DOM attribute alone is not enough — delete the internal
      // flag so Alpine's walker descends into children on initTree().
      delete el._x_ignore;
      el.setAttribute('x-data', xDataValue);

      // Ask Alpine to initialise this subtree
      if (typeof Alpine !== 'undefined') {
        Alpine.initTree(el);
      }
    };
  }

  /**
   * Wire up the correct trigger for a single element.
   *
   * @param {Element} el
   * @param {string}  mode  — 'intersect' | 'interact' | 'idle' | 'event:name'
   */
  function attachDefer(el, mode) {
    const hydrate = defer(el);

    // ── intersect ────────────────────────────────────────────────────────
    if (mode === 'intersect') {
      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              obs.disconnect();
              hydrate();
            }
          });
        },
        { rootMargin: '0px' },
      );
      io.observe(el);
      return;
    }

    // ── interact ─────────────────────────────────────────────────────────
    if (mode === 'interact') {
      const events = ['touchstart', 'mouseover', 'wheel', 'scroll', 'keydown'];
      const once = { once: true, passive: true };

      function onInteract() {
        events.forEach((ev) => window.removeEventListener(ev, onInteract));
        hydrate();
      }

      events.forEach((ev) => window.addEventListener(ev, onInteract, once));
      return;
    }

    // ── idle ─────────────────────────────────────────────────────────────
    if (mode === 'idle') {
      const cb = () => hydrate();

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(cb, { timeout: IDLE_TIMEOUT });
      } else {
        // Safari fallback — use a plain timeout
        setTimeout(cb, IDLE_TIMEOUT);
      }
      return;
    }

    // ── event:name ───────────────────────────────────────────────────────
    if (mode.startsWith('event:')) {
      const eventName = mode.slice('event:'.length);
      window.addEventListener(eventName, hydrate, { once: true });
      return;
    }

    // Unknown mode — hydrate immediately so we degrade gracefully
    console.warn('[alpine-defer] Unknown mode "%s" on element', mode, el);
    hydrate();
  }

  /**
   * Scan the DOM for `[x-defer]` elements and wire up their triggers.
   * Called once at `alpine:init` time — before Alpine walks the DOM.
   */
  document.addEventListener('alpine:init', () => {
    document.querySelectorAll('[x-defer]').forEach((el) => {
      const mode = el.getAttribute('x-defer') || 'interact';
      el.removeAttribute('x-defer'); // clean up — no longer needed
      attachDefer(el, mode);
    });
  });

  /**
   * Public API: window.GJ.defer(el, mode)
   * Allows programmatic deferral from Liquid snippets that know their own mode.
   */
  window.GJ = window.GJ || {};
  window.GJ.defer = attachDefer;
})();
